import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Card,
  CardContent,
  Stack,
  IconButton,
  Icon,
  Tooltip,
  Button,
  MenuItem,
  Menu
} from 'investira.react.components';
import { dates, arrays, formats, validators } from 'investira.sdk';
import { HighchartsReact } from 'investira.react.charts';
import { CONSOLIDACAO_TIPO } from '@investirapri/financa.js/lib/enums';
import PropTypes from 'prop-types';
import services from '../../../services';
import { useEmpresasContext } from '../../../contexts/EmpresasContext';
import CardState from './CardState';

const PatrimonioCard = memo(props => {
  const params = useParams();
  const {
    pessoaId,
    patrimonioLiquido,
    actions: pActions,
    onOpenHistory,
    showChart = true,
    showHistoryButton = true,
    sx,
    ...otherProps
  } = props;

  // Tenta obter o contexto, mas define um fallback seguro caso não esteja dentro do Provider
  const xContext = useEmpresasContext();
  const { state, actions: xContextActions = {} } = xContext || {};

  const xPessoaId = pessoaId || params?.pessoa_id;

  const [patrimonio, setPatrimonio] = useState({});
  const [historicoLocal, setHistoricoLocal] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [consolidacaoTipo, setConsolidacaoTipo] = useState();
  const [anchorElConsolidaco, setAnchorElConsolidaco] = useState(null);
  const [options, setOptions] = useState([]);

  const openConsolidacao = Boolean(anchorElConsolidaco);
  const xPatrimonio = patrimonio?.[consolidacaoTipo];
  // Decide se usa o histórico do contexto ou o local
  const xHistorico = validators.isEmpty(state?.historico?.pl) ? historicoLocal : state.historico.pl;

  const formatData = useCallback((pData = [], pConsolidacaoTipo) => {
    if (validators.isNull(pConsolidacaoTipo) || validators.isEmpty(pData)) {
      return [];
    }

    return pData
      .filter(xItem => xItem?.consolidacao_tipo === pConsolidacaoTipo)
      .map(xPoint => [dates.toDate(xPoint.data).getTime(), xPoint.pl]);
  }, []);

  const xChartOptions = useMemo(
    () => ({
      title: false,
      chart: {
        height: 120,
        margin: [0, 0, 0, 0],
        spacing: [0, 0, 0, 0]
      },
      legend: { enabled: false },
      xAxis: { visible: false },
      yAxis: { visible: false },
      responsive: {
        condition: {
          maxWidth: 500
        }
      },
      series: [
        {
          name: 'PL',
          type: 'line',
          data: formatData(xHistorico, consolidacaoTipo),
          marker: {
            enabled: false
          },
          zones: [
            {
              value: 0,
              color: '#ed442c'
            },
            {
              color: '#00dfa8'
            }
          ]
        }
      ],
      tooltip: {
        formatter: function () {
          const xDate = formats.formatDateCustom(this.point.x, 'DD/MMM/YYYY');
          const xValue = formats.friendlyNumber(this.point.y, 2, false);
          const xFullValue = formats.formatNumber(this.point.y, 2, true, false);

          return `<span style="font-size: 1em"><b>${xDate}</b></span><br/><span style="color:${this.color}">●</span>${this.series.name}: <b>${xValue}</b> (${xFullValue})<br/>`;
        }
      }
    }),
    [consolidacaoTipo, formatData, xHistorico]
  );

  useEffect(() => {
    // Se não tem pessoaId (nem por prop, nem por param), não faz nada.
    if (!xPessoaId) {
      setLoading(false);
      return;
    }

    // Se o componente está dentro do EmpresasProvider, os dados virão do contexto.
    if (xContext) {
      // Se o histórico do contexto ainda não carregou, exibe o loading e aguarda.
      if (validators.isEmpty(state?.historico?.pl)) {
        setLoading(true);
      } else {
        // Se o histórico carregou, processa os dados para obter os valores mais recentes.
        // O histórico vem ordenado por data ASC, então arrayToObject pegará o último (mais recente).
        const xData = arrays.arrayToObject(state.historico.pl, 'consolidacao_tipo');
        const xNewOptions = Array.from(
          new Set(state.historico.pl.map(xElem => xElem.consolidacao_tipo))
        );

        setPatrimonio(xData);
        setOptions(xNewOptions);

        const xDefault = state.config?.consolidacao_tipo || xNewOptions[0];
        setConsolidacaoTipo(xDefault);
        setLoading(false);
      }
      return; // Impede a execução da busca de dados do modo standalone.
    }

    // Lógica para modo standalone (quando não há contexto)
    setLoading(true);
    services.pessoas.pl(
      {
        data: dates.toSqlDate(dates.toDate()),
        pessoa_id: xPessoaId
      },
      rRes => {
        if (validators.isEmpty(rRes?.data)) {
          setPatrimonio({});
          setOptions([]);
          setConsolidacaoTipo(null);
          return;
        }

        const xData = arrays.arrayToObject(rRes.data, 'consolidacao_tipo');
        const xNewOptions = Array.from(new Set(rRes.data.map(xElem => xElem.consolidacao_tipo)));

        setPatrimonio(xData);
        setOptions(xNewOptions);

        const xDefault = xNewOptions[0];
        setConsolidacaoTipo(prev => prev || xDefault);
      },
      null,
      () => {
        setLoading(false);
      }
    );

    return () => services.pessoas?.cancel?.pl?.cancel && services.pessoas.cancel.pl.cancel();
  }, [xPessoaId, xContext, state?.historico?.pl, state?.config?.consolidacao_tipo]);

  // useEffect para buscar histórico localmente caso não exista no contexto
  useEffect(() => {
    if (!xPessoaId || !validators.isEmpty(state?.historico?.pl)) {
      return;
    }

    const xToday = new Date();

    services.pessoas.pl(
      {
        data: null,
        data_inicio: dates.addYears(dates.toSqlDate(xToday), -10),
        data_fim: dates.toSqlDate(xToday),
        pessoa_id: xPessoaId,
        sort: 'data ASC'
      },
      rRes => {
        setHistoricoLocal(rRes?.data || []);
      },
      null,
      () => {}
    );
  }, [xPessoaId, state?.historico?.pl]);

  const xValue = !validators.isNull(patrimonioLiquido) ? patrimonioLiquido : xPatrimonio?.pl;
  const xShowCard = !validators.isNull(xValue) || !validators.isEmpty(xPatrimonio);

  const formatNumber = pValue => {
    if (validators.isNull(pValue)) {
      return '-';
    }

    // Prioridade 1: Formatador passado via props
    if (pActions?.formatNumber) {
      return pActions.formatNumber(pValue);
    }

    // Prioridade 2: Formatador do contexto
    if (xContextActions?.formatNumber) {
      return xContextActions.formatNumber(pValue, state?.showPrecise, true, 2, null);
    }

    // Prioridade 3: Formatador padrão (sem contexto)
    return formats.friendlyNumber(pValue, 2, false);
  };

  const handleOpenClick = () => {
    // Prioridade 1: Handler passado via props
    if (onOpenHistory) {
      return onOpenHistory('pl');
    }

    // Prioridade 2: Action do contexto
    xContextActions?.handleChartVariantChange && xContextActions.handleChartVariantChange('pl');
  };

  function renderConsolidacaoMenu(pConsolidacaoTipo, pOptions = [], pCurrentConsolidacao) {
    if (validators.isEmpty(pOptions)) {
      return null;
    }

    const xMenu = pOptions.map(xId => {
      const xConsolidacaoTipo = Object.values(pConsolidacaoTipo).find(xItem => xItem.id === xId);

      return {
        label: xConsolidacaoTipo?.descricao,
        value: xConsolidacaoTipo?.id
      };
    });

    const xCurrentLabel = Object.values(pConsolidacaoTipo).find(
      xItem => xItem.id === pCurrentConsolidacao
    );

    const xProps =
      pOptions.length >= 2
        ? { onClick: pEvent => setAnchorElConsolidaco(pEvent.currentTarget) }
        : { disabled: true };

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          disableElevation
          {...xProps}
          sx={{ fontSize: '0.7rem', padding: '3px 8px' }}>
          {xCurrentLabel?.descricao}
        </Button>
        <Menu
          anchorEl={anchorElConsolidaco}
          open={openConsolidacao}
          onClose={() => setAnchorElConsolidaco(null)}>
          {xMenu.map(xItem => (
            <MenuItem
              key={xItem.value}
              onClick={() => {
                setConsolidacaoTipo(xItem.value);
                setAnchorElConsolidaco(null);
                // Atualiza contexto se disponível
                xContextActions?.handleConfigChange &&
                  xContextActions.handleConfigChange({ consolidacao_tipo: xItem.value });
              }}>
              {xItem.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  if (!isLoading && !xShowCard) {
    return null;
  }

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 0,
        ...sx
      }}
      aria-label={
        state?.pessoa?.setor_id === '65.41.3.00' ? 'Patrimônio Social' : 'Patrimônio Líquido'
      }
      {...otherProps}>
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" spacing={2}>
            <Stack flexGrow={1}>
              <Typography variant="caption" color="textPrimary" component="h4" gutterBottom>
                {state?.pessoa?.setor_id === '65.41.3.00'
                  ? 'Patrimônio Social'
                  : 'Patrimônio líquido'}
              </Typography>
            </Stack>
            {xShowCard && showHistoryButton && (
              <Stack>
                <Tooltip title="Histórico Patrimônio Líquido">
                  <div>
                    <IconButton color="primary" onClick={handleOpenClick}>
                      <Icon iconName="chart_bar" size={16} />
                    </IconButton>
                  </div>
                </Tooltip>
              </Stack>
            )}
          </Stack>

          <CardState
            loading={isLoading}
            data={!validators.isNull(xValue) ? { pl: xValue } : xPatrimonio}>
            <Stack spacing={1} justifyContent="space-between">
              <Stack>
                <Typography variant="h4" color="textPrimary" sx={{ fontWeight: 600 }}>
                  {formatNumber(xValue)}
                </Typography>

                {!validators.isNull(xPatrimonio?.data) && (
                  <Typography variant="caption" color="textPrimary" gutterBottom>
                    Data de referência {formats.formatDateCustom(xPatrimonio?.data, 'DD/MMM/YYYY')}
                  </Typography>
                )}
              </Stack>

              {showChart && (
                <>
                  <Stack flexGrow="1">
                    <HighchartsReact options={xChartOptions} />
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Stack>
                      {renderConsolidacaoMenu(CONSOLIDACAO_TIPO, options, consolidacaoTipo)}
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </CardState>
        </CardContent>
      </Card>
    </Stack>
  );
});

PatrimonioCard.displayName = 'PatrimonioCard';

PatrimonioCard.propTypes = {
  pessoaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  patrimonioLiquido: PropTypes.number,
  actions: PropTypes.shape({
    formatNumber: PropTypes.func
  }),
  onOpenHistory: PropTypes.func,
  showChart: PropTypes.bool,
  sx: PropTypes.object,
  showHistoryButton: PropTypes.bool
};

export default PatrimonioCard;
